import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Star, Loader2, Check, AlertCircle, Heart } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import Seo from '../components/Seo';
import { countriesFor, countryName } from '../lib/countries';
import { Flag } from '../components/Flag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  es: {
    kicker: 'Tu opinión',
    title: 'Hola {name}, ¿cómo ha ido tu experiencia?',
    intro: 'Tu reseña ayuda a otras personas a decidirse a aprender español. Solo te llevará un minuto.',
    ratingLabel: '¿Qué puntuación le pones a las clases?',
    textLabel: 'Cuéntame tu experiencia',
    placeholder: 'Lo que más me ha gustado de las clases es...',
    nameLabel: '¿Cómo quieres que aparezca tu nombre?',
    first: 'Solo mi nombre de pila',
    full: 'Mi nombre completo',
    countryLabel: '¿De qué país eres? (opcional)',
    countryPlaceholder: 'Elige tu país',
    submit: 'Enviar mi reseña',
    thanksTitle: '¡Muchísimas gracias!',
    thanks: 'He recibido tu reseña. La revisaré antes de publicarla en la web.',
    already: 'Ya habías enviado tu reseña. ¡Mil gracias por tu tiempo!',
    invalid: 'Enlace no válido',
    invalidNote: 'Revisa el enlace que te envié por email.',
    minText: 'Escribe al menos 20 caracteres.',
    noRating: 'Elige una puntuación.',
    note: 'Reviso cada reseña antes de publicarla.',
  },
  en: {
    kicker: 'Your review',
    title: 'Hi {name}, how was your experience?',
    intro: 'Your review helps other people decide to start learning Spanish. It only takes a minute.',
    ratingLabel: 'How would you rate the lessons?',
    textLabel: 'Tell me about your experience',
    placeholder: 'What I liked most about the lessons is...',
    nameLabel: 'How would you like your name to appear?',
    first: 'Just my first name',
    full: 'My full name',
    countryLabel: 'Where are you from? (optional)',
    countryPlaceholder: 'Choose your country',
    submit: 'Send my review',
    thanksTitle: 'Thank you so much!',
    thanks: 'I have received your review. I will read it before publishing it on the website.',
    already: 'You already sent your review. Thank you for your time!',
    invalid: 'Invalid link',
    invalidNote: 'Please check the link I sent you by email.',
    minText: 'Please write at least 20 characters.',
    noRating: 'Please choose a rating.',
    note: 'I read every review before publishing it.',
  },
  de: {
    kicker: 'Deine Bewertung',
    title: 'Hallo {name}, wie war deine Erfahrung?',
    intro: 'Deine Bewertung hilft anderen bei der Entscheidung, Spanisch zu lernen. Es dauert nur eine Minute.',
    ratingLabel: 'Wie bewertest du den Unterricht?',
    textLabel: 'Erzähl mir von deiner Erfahrung',
    placeholder: 'Am besten gefallen hat mir am Unterricht...',
    nameLabel: 'Wie soll dein Name erscheinen?',
    first: 'Nur mein Vorname',
    full: 'Mein vollständiger Name',
    countryLabel: 'Aus welchem Land kommst du? (optional)',
    countryPlaceholder: 'Land auswählen',
    submit: 'Bewertung senden',
    thanksTitle: 'Ganz herzlichen Dank!',
    thanks: 'Ich habe deine Bewertung erhalten und schaue sie mir an, bevor ich sie veröffentliche.',
    already: 'Du hast deine Bewertung bereits gesendet. Vielen Dank für deine Zeit!',
    invalid: 'Ungültiger Link',
    invalidNote: 'Bitte prüfe den Link, den ich dir per E-Mail geschickt habe.',
    minText: 'Bitte schreibe mindestens 20 Zeichen.',
    noRating: 'Bitte wähle eine Bewertung.',
    note: 'Ich lese jede Bewertung, bevor ich sie veröffentliche.',
  },
};

export default function ReviewFormPage() {
  const { token } = useParams();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [text, setText] = useState('');
  const [country, setCountry] = useState('');
  const [nameMode, setNameMode] = useState('first');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/by-token/${token}`);
        setReview(data);
      } catch {
        setReview(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Si no se puede detectar el idioma del alumno, español por defecto.
  const c = COPY[review?.language] || COPY.es;

  const submit = async () => {
    setError('');
    if (!rating) return setError(c.noRating);
    if (text.trim().length < 20) return setError(c.minText);
    setSaving(true);
    try {
      await axios.post(`${API}/reviews/by-token/${token}`, {
        rating,
        text: text.trim(),
        display_name_mode: nameMode,
        country: country ? countryName(country, review.language || 'es') : null,
        country_code: country || null,
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'No hemos podido enviar tu reseña. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E8F5FA]">
        <Seo path={`/opinion/${token}`} title="Tu opinión" noindex />
        <Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" />
      </div>
    );
  }

  if (!review) {
    return (
      <div className="min-h-screen bg-white">
        <Seo path={`/opinion/${token}`} title="Tu opinión" noindex />
        <Navigation />
        <div className="pt-36 pb-24 px-6 text-center" data-testid="review-invalid">
          <AlertCircle className="w-12 h-12 text-[#E38987] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#4A4A4A] mb-2">{COPY.es.invalid}</h1>
          <p className="text-gray-600">{COPY.es.invalidNote}</p>
        </div>
        <Footer />
      </div>
    );
  }

  const submitted = done || review.already_submitted;

  return (
    <div className="min-h-screen bg-white">
      <Seo path={`/opinion/${token}`} title="Tu opinión" noindex />
      <Navigation />

      <section className="pt-32 pb-20 bg-gradient-to-b from-[#E8F5FA] to-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 text-center"
              data-testid="review-thanks"
            >
              <Heart className="w-12 h-12 text-[#F5A6A4] mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-[#4A4A4A] mb-3">{c.thanksTitle}</h1>
              <p className="text-gray-600">{done ? c.thanks : c.already}</p>
            </motion.div>
          ) : (
            <>
              <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">{c.kicker}</span>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#4A4A4A] mt-3 mb-3" data-testid="review-title">
                {c.title.replace('{name}', review.name)}
              </h1>
              <p className="text-gray-600 mb-8">{c.intro}</p>

              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-gray-100 space-y-6">
                <div>
                  <p className="text-sm font-bold text-[#4A4A4A] mb-3">{c.ratingLabel}</p>
                  <div className="flex gap-1" onMouseLeave={() => setHovered(0)}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHovered(n)}
                        data-testid={`review-star-${n}`}
                        className="p-1 transition-transform hover:scale-110"
                        aria-label={`${n}/5`}
                      >
                        <Star
                          className={`w-8 h-8 ${(hovered || rating) >= n ? 'text-[#F5C14B] fill-[#F5C14B]' : 'text-gray-300'}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold text-[#4A4A4A] mb-2 block" htmlFor="review-text">{c.textLabel}</label>
                  <textarea
                    id="review-text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={6}
                    placeholder={c.placeholder}
                    data-testid="review-text"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none resize-none text-[#4A4A4A]"
                  />
                </div>

                <div>
                  <p className="text-sm font-bold text-[#4A4A4A] mb-2">{c.nameLabel}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[['first', c.first], ['full', c.full]].map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setNameMode(mode)}
                        data-testid={`review-name-${mode}`}
                        className={`py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
                          nameMode === mode
                            ? 'bg-[#A8D8EA] text-white shadow-md'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold text-[#4A4A4A] mb-2 block" htmlFor="review-country">{c.countryLabel}</label>
                  <select
                    id="review-country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    data-testid="review-country"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none text-[#4A4A4A] bg-white"
                  >
                    <option value="">{c.countryPlaceholder}</option>
                    {countriesFor(review.language || 'es').map((item) => (
                      <option key={item.code} value={item.code}>
                        {item[review.language] || item.es}
                      </option>
                    ))}
                  </select>
                  {country && (
                    <p className="mt-2 text-sm text-gray-500 flex items-center gap-2" data-testid="review-country-preview">
                      <Flag code={country} title={countryName(country, review.language || 'es')} />
                      {countryName(country, review.language || 'es')}
                    </p>
                  )}
                </div>

                {error && <p className="text-sm text-red-500 font-semibold" data-testid="review-error">{error}</p>}

                <button
                  onClick={submit}
                  disabled={saving}
                  data-testid="review-submit"
                  className="w-full py-3.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  {c.submit}
                </button>
                <p className="text-xs text-gray-400 text-center">{c.note}</p>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
